import { formatTrackingNumberWithEllipsis } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { Package } from '@frontend/package/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { SpotClient } from '@frontend/spot/api';
import { Spot } from '@frontend/spot/types';
import { TransactionClient } from '@frontend/transaction/api';
import { Transaction, TransactionStateName } from '@frontend/transaction/types';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';

import { getMoveOpeningSlotCount, isMovableTransactionState, useMoveTransaction } from '../../hooks/useMoveTransaction';
import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';

export type MoveConfirmPhase = 'confirm' | 'opening';

export interface MoveConfirmViewProps {
    phase: MoveConfirmPhase;
    transaction: Transaction | null;
    destinationSpot: Spot | null;
    destinationSlot: Slot | null;
    openingPackages: Package[];
    trackingNumber: string;
    isLoading: boolean;
    isSubmitting: boolean;
    error: ReactNode | null;
    canConfirm: boolean;
    handleConfirm: () => Promise<void>;
    handleOpeningContinue: () => void;
    handleBack: () => void;
}

export const useMoveConfirm = (): MoveConfirmViewProps => {
    const {
        slotId: sourceSlotId,
        transactionId,
        destSpotId,
        destSlotId
    } = useParams<{
        slotId: string;
        transactionId: string;
        destSpotId: string;
        destSlotId: string;
    }>();
    const navigate = useKioskNavigate();
    const navigationState = useAppSelector((state) => state.navigation);
    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const { executeMove, isSubmitting, error: moveError, setError: setMoveError } = useMoveTransaction();

    const [phase, setPhase] = useState<MoveConfirmPhase>('confirm');
    const [openingPackages, setOpeningPackages] = useState<Package[]>([]);
    const [transaction, setTransaction] = useState<Transaction | null>(null);
    const [destinationSpot, setDestinationSpot] = useState<Spot | null>(null);
    const [destinationSlot, setDestinationSlot] = useState<Slot | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<ReactNode | null>(null);

    useEffect(() => {
        if (!accountId || !transactionId || !destSpotId || !destSlotId) {
            setIsLoading(false);
            setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);
        setMoveError(null);

        Promise.all([
            TransactionClient.fetchTransaction(accountId, transactionId),
            SpotClient.resolveSpots([destSpotId]).then((r) => r.results[0] ?? null),
            SlotClient.fetchAccountSpotSlot(accountId, destSpotId, destSlotId)
        ])
            .then(([trx, spot, slot]) => {
                if (cancelled) return;
                if (!isMovableTransactionState(trx.state as TransactionStateName) || !spot || !slot) {
                    setError(CommonMessage.ERRORS.DEFAULT);
                    setTransaction(null);
                    return;
                }
                if (slot.id === sourceSlotId && destSpotId === trx.spot_id) {
                    setError(CommonMessage.ERRORS.DEFAULT);
                    setTransaction(null);
                    return;
                }
                setTransaction(trx);
                setDestinationSpot(spot);
                setDestinationSlot(slot);
            })
            .catch((err) => {
                if (cancelled) return;
                Logger.error('Failed to load move transaction confirm', err);
                setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [accountId, transactionId, destSpotId, destSlotId, sourceSlotId, setMoveError]);

    const navigateAfterDropOffMove = useCallback(
        (trx: Transaction, destSlot: Slot) => {
            const isSameSpot = destSlot.spot_id === trx.spot_id;
            if (isSameSpot) {
                navigate(`/spots/layout/slot/${destSlot.id}`);
                return;
            }
            navigate('/spots/layout/pudo/awaiting-drop-off');
        },
        [navigate]
    );

    const handleConfirm = useCallback(async () => {
        if (!accountId || !transaction || !destinationSlot) return;

        const transactionState = transaction.state as TransactionStateName;
        const currentSlotId = transaction.slot_id ?? sourceSlotId;
        const openingSlotCount = getMoveOpeningSlotCount(transactionState);
        const isPickUpPending = openingSlotCount === 1;

        // Mount MQTT open/close UI before PACKAGE_SKIP so workflow door events are not missed.
        // Only open the expected count of source SLOTs (never the destination).
        if (openingSlotCount > 0 && currentSlotId) {
            flushSync(() => {
                setOpeningPackages([{ slot_id: currentSlotId } as Package]);
                setPhase('opening');
            });
        }

        const result = await executeMove({
            accountId,
            transactionId: transaction.id,
            transactionState,
            transactionUserId: transaction.user_id,
            currentSpotId: transaction.spot_id,
            currentSlotId,
            destination: {
                spotId: destinationSlot.spot_id,
                moduleId: destinationSlot.module_id,
                slotId: destinationSlot.id
            }
        });

        if (!result) {
            if (isPickUpPending) {
                setPhase('confirm');
                setOpeningPackages([]);
            }
            return;
        }

        // Pick-up pending: stay on open/close UI (source SLOT already tracked).
        if (isPickUpPending) {
            return;
        }

        navigateAfterDropOffMove(transaction, destinationSlot);
    }, [accountId, transaction, destinationSlot, executeMove, sourceSlotId, navigateAfterDropOffMove]);

    const handleOpeningContinue = useCallback(() => {
        if (!transactionId) return;
        navigate(`/spots/layout/pudo/transactions/${transactionId}`);
    }, [navigate, transactionId]);

    const handleBack = useCallback(() => {
        if (phase === 'opening') return;
        navigate(-1);
    }, [navigate, phase]);

    return {
        phase,
        transaction,
        destinationSpot,
        destinationSlot,
        openingPackages,
        trackingNumber: formatTrackingNumberWithEllipsis(transaction?.tracking_number ?? '') || '/',
        isLoading,
        isSubmitting,
        error: error ?? moveError,
        canConfirm: !!transaction && !!destinationSlot && !error && !isSubmitting && phase === 'confirm',
        handleConfirm,
        handleOpeningContinue,
        handleBack
    };
};
